import { DOCUMENT, NgIf } from '@angular/common';
import { Component, HostListener, Inject, OnDestroy, OnInit, Renderer2, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { FuseConfig, FuseConfigService } from '@fuse/services/config';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { FusePlatformService } from '@fuse/services/platform';
import { FUSE_VERSION } from '@fuse/version';
import { combineLatest, filter, map, Subject, takeUntil } from 'rxjs';
import { SettingsComponent } from './common/settings/settings.component';
import { EmptyLayoutComponent } from './layouts/empty/empty.component';
import { CenteredLayoutComponent } from './layouts/horizontal/centered/centered.component';
import { EnterpriseLayoutComponent } from './layouts/horizontal/enterprise/enterprise.component';
import { MaterialLayoutComponent } from './layouts/horizontal/material/material.component';
import { ModernLayoutComponent } from './layouts/horizontal/modern/modern.component';
import { ClassicLayoutComponent } from './layouts/vertical/classic/classic.component';
import { ClassyLayoutComponent } from './layouts/vertical/classy/classy.component';
import { CompactLayoutComponent } from './layouts/vertical/compact/compact.component';
import { DenseLayoutComponent } from './layouts/vertical/dense/dense.component';
import { FuturisticLayoutComponent } from './layouts/vertical/futuristic/futuristic.component';
import { ThinLayoutComponent } from './layouts/vertical/thin/thin.component';
import { Util } from 'app/models/util';
import { UserImpl } from 'app/models/user';
import { EventService } from 'app/services/event.service';
import { UploadQueuePopupComponent } from 'app/modules/admin/apps/file-manager/upload-queue-popup/upload-queue-popup.component';
import { FileUploadQueueService } from 'app/services/file-upload-queue.service';

@Component({
    selector     : 'layout',
    templateUrl  : './layout.component.html',
    styleUrls    : ['./layout.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone   : true,
    imports      : [
        NgIf, 
        EmptyLayoutComponent, 
        CenteredLayoutComponent, 
        EnterpriseLayoutComponent, 
        MaterialLayoutComponent, 
        ModernLayoutComponent, 
        ClassicLayoutComponent, 
        ClassyLayoutComponent, 
        CompactLayoutComponent, 
        DenseLayoutComponent, 
        FuturisticLayoutComponent, 
        ThinLayoutComponent, 
        SettingsComponent,
        UploadQueuePopupComponent
    ],
})
export class LayoutComponent implements OnInit, OnDestroy
{
    config: FuseConfig;
    layout: string;
    scheme = 'light';
    theme = 'theme-default';
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    private user = new UserImpl();
    userId = null;
    last = null;

    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        @Inject(DOCUMENT) private _document: any,
        private _renderer2: Renderer2,
        private _router: Router,
        private _fuseConfigService: FuseConfigService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fusePlatformService: FusePlatformService,
        private util : Util,
        private eventServ : EventService,
        private fileUploadService : FileUploadQueueService,
        
    )
    {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */

    @HostListener('window:popstate', ['$event'])
    onBrowserBackBtnClose(event: Event) {
    this.showUploadAlert();    
    }

    @HostListener('window:pushstate', ['$event'])
    onBrowserFwdBtnClose(event: Event) {
    this.showUploadAlert();    
    }
    
    ngOnInit(): void
    {
        
       window.addEventListener('beforeunload', this.onBeforeUnload);
  
       this.util.setIsWebApp(false);
       this.user = this.util.getUserFromSession();
       this.userId = this.user ? this.user.id : null;
       this.util.setUserId(this.userId);
       this.eventServ.onThemePrefChange.subscribe((user)=>{
        if(user) this.user = user;
        this.setPreference();
       })

       this.setPreference();
            // Store the options
      
            // Update the scheme and theme

        // Subscribe to config changes
        this._fuseConfigService.config$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((config: FuseConfig) =>
            {
                // Store the config
                this.config = config;

                // Update the layout
                this._updateLayout();
            });

        // Subscribe to NavigationEnd event
        this._router.events.pipe(
            filter(event => event instanceof NavigationEnd),
            takeUntil(this._unsubscribeAll),
        ).subscribe(() =>
        {
            // Update the layout
            this._updateLayout();
        });

        // Set the app version
        this._renderer2.setAttribute(this._document.querySelector('[ng-version]'), 'fuse-version', FUSE_VERSION);

        // Set the OS name
        this._renderer2.addClass(this._document.body, this._fusePlatformService.osName);
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    setPreference(){
        
        this.scheme = this.user && this.user.scheme ?  this.user.scheme : this.scheme;
        this.theme = this.user && this.user.theme ?  this.user.theme : this.theme;
        console.log("layout.component _setPreference");
        this._updateScheme();
        this._updateTheme();

    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Update the selected layout
     */
    private _updateLayout(): void
    {
        // Get the current activated route
        let route = this._activatedRoute;
        while ( route.firstChild )
        {
            route = route.firstChild;
        }

        // 1. Set the layout from the config
        this.layout = this.config.layout;

        // 2. Get the query parameter from the current route and
        // set the layout and save the layout to the config
        const layoutFromQueryParam = route.snapshot.queryParamMap.get('layout');
        if ( layoutFromQueryParam )
        {
            this.layout = layoutFromQueryParam;
            if ( this.config )
            {
                this.config.layout = layoutFromQueryParam;
            }
        }

        // 3. Iterate through the paths and change the layout as we find
        // a config for it.
        //
        // The reason we do this is that there might be empty grouping
        // paths or componentless routes along the path. Because of that,
        // we cannot just assume that the layout configuration will be
        // in the last path's config or in the first path's config.
        //
        // So, we get all the paths that matched starting from root all
        // the way to the current activated route, walk through them one
        // by one and change the layout as we find the layout config. This
        // way, layout configuration can live anywhere within the path and
        // we won't miss it.
        //
        // Also, this will allow overriding the layout in any time so we
        // can have different layouts for different routes.
        const paths = route.pathFromRoot;
        paths.forEach((path) => {
            // Check if there is a 'layout' data
            if ( path.routeConfig && path.routeConfig.data && path.routeConfig.data.layout ) {
                // Set the layout
                this.layout = path.routeConfig.data.layout;
            }
        });
    }

    /**
     * Update the selected scheme
     *
     * @private
     */
    private _updateScheme(): void {
         // If the currently selected scheme already exists, do nothing.
         /*
         if (this._document.body.classList.contains('dark') || this._document.body.classList.contains('light')) {
            console.log("layout.component: hasScheme");
            return;
        }
        */
        // Remove class names for all schemes
        this._document.body.classList.remove('light', 'dark');
     
        // Add class name for the currently selected scheme
        this._document.body.classList.add(this.scheme);
    }

    /**
     * Update the selected theme
     *
     * @private
     */
    private _updateTheme(): void {
        /*
        const hasThemeClass = Array.from(this._document.body.classList).some((className: string) => className.startsWith('theme-'));
        if (hasThemeClass) {
            console.log("layout.component: hasThemeClass");
            return;
        }
        */
        // Find the class name for the previously selected theme and remove it
        this._document.body.classList.forEach((className: string) => {
            if ( className.startsWith('theme-') ) {
                this._document.body.classList.remove(className, className.split('-')[1]);
            }
        });

        // Add class name for the currently selected theme
        this._document.body.classList.add(this.theme);
}

    onBeforeUnload = (event: BeforeUnloadEvent) => {
        if ( this.fileUploadService.uploader?.queue.length > 0 && this.fileUploadService?.uploader?.isUploading) {
            event.preventDefault();
            event.returnValue = '';
        }
    };

    showUploadAlert(): void {
        if (this.util?.fileUploader?.isUploading) {
            alert("Leaving this page may cancel your upload.");
        }
    }

}
